import type { RoomArtRow } from "../lib/room-state";
import { ROOM_ART_MOBILE_MEDIA, verticalSlug } from "../roomArt.js";

// HER ORIENTATION LAW (2026-08-07) — **THE TWO PLACES ROOM ART IS PAINTED, AND THEY MUST DECIDE THE
// SAME WAY.**
//
// The backdrop and the picker's thumbnails are one question asked twice: *which art does THIS screen
// have for this slug?* Round one of this order fixed the backdrop and left the thumbnails alone, so a
// phone was offered the right sixty choices and shown all sixty as landscape pictures — *"nobody ever
// sees the other orientation"* broken at the last inch. They are one file now.
//
// **BOTH ARE HOOKLESS, so `scripts/room-art-orientation.mjs` CALLS them** and reads the tree back —
// including the state a screenshot can never show, which is what the backdrop draws for a choice this
// screen has no art for. They came out of `src/App.tsx` for that reason and for the ordinary one: it
// is the largest standing debt in the repo and this round was growing it.
//
// **A 1x1 TRANSPARENT GIF STANDS IN THE DESKTOP SLOT OF VERTICAL-ONLY ART.** `<picture>` renders
// nothing at all without an `<img>`, and what should show there is the CSS backdrop underneath.
export const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/**
 * **THE ROOM'S BACKDROP.** M4's `<picture>` lets the BROWSER pick vertical or wide with no JS swap
 * that could flash — and each slot is drawn only when that art EXISTS. Drawing both unconditionally
 * is what stretched portrait art across a desktop, which is the defect she reported.
 *
 * `art` is the member's CURRENT row, which the server sends whether or not this viewport offers it.
 * When it has not arrived, or belongs to a different slug, both slots are drawn — that is exactly
 * today's behaviour for a choice nobody has measured, and it is the safe direction: the alternative
 * is a room that goes dark while a payload is in flight.
 */
export function RoomBackdrop({
  slug, art, url,
}: {
  slug: string; art: RoomArtRow | null; url: (s: string) => string;
}) {
  if (!slug) return <div className="room-backdrop" aria-hidden="true" />;
  const known = art && art.slug === slug;
  const hasWide = !known || art.hasWide !== false;
  const hasVertical = !known || art.hasVertical !== false;
  if (!hasWide && !hasVertical) return <div className="room-backdrop" aria-hidden="true" />;
  return (
    <picture className="room-backdrop" aria-hidden="true">
      {hasVertical ? <source media={ROOM_ART_MOBILE_MEDIA} srcSet={url(verticalSlug(slug))} /> : null}
      <img src={hasWide ? url(slug) : TRANSPARENT_PIXEL} alt="" />
    </picture>
  );
}

/**
 * **ONE THUMBNAIL IN THE PICKER, AND IT IS THE ART FOR THIS SCREEN.** Same `<picture>` mechanism as
 * the backdrop, so the browser decides and it stays right when a tablet rotates across the line.
 */
export function RoomArtThumb({ art, url }: { art: RoomArtRow; url: (s: string) => string }) {
  return (
    <picture>
      {art.hasVertical ? <source media={ROOM_ART_MOBILE_MEDIA} srcSet={url(verticalSlug(art.slug))} /> : null}
      <img src={url(art.hasWide ? art.slug : verticalSlug(art.slug))} alt="" loading="lazy" />
    </picture>
  );
}

/**
 * **THE WHOLE GRID SHE PICKS FROM.** It came out of App.tsx with the two painters above it — one
 * concern, one file, and the R8 ratchet paid rather than waived. Hookless, so the gate CALLS it: the
 * list it draws is already viewport-scoped by the server, and what this has to get right is that the
 * SELECTED one is marked and every tile is a real control with a name a screen reader can read.
 *
 * The label is a FUNCTION rather than a string, because the copy is translated and this file must not
 * learn about languages to render a grid (ITEM 14a: the house ships in two).
 */
export function RoomArtPicker({
  backgrounds, selected, url, onChoose, label,
}: {
  backgrounds: RoomArtRow[]; selected: string; url: (s: string) => string;
  onChoose: (slug: string) => void; label: (name: string) => string;
}) {
  return (
    <div className="room-bg-grid">
      {backgrounds.map((bg) => (
        <button
          key={bg.slug}
          type="button"
          className={selected === bg.slug ? "room-bg-thumb room-bg-thumb--active" : "room-bg-thumb"}
          onClick={() => onChoose(bg.slug)}
          aria-pressed={selected === bg.slug}
          aria-label={label(bg.slug.replace("-", " "))}
        >
          <RoomArtThumb art={bg} url={url} />
        </button>
      ))}
    </div>
  );
}
